// LocalStorage utility functions for TradeCopilot

const STORAGE_KEYS = {
  MARKET_CONDITIONS: 'tradeCopilot_marketConditions',
  TRADE_JOURNAL: 'tradeCopilot_tradeJournal',
  TRADING_PLAN: 'tradeCopilot_tradingPlan',
  VALIDATED_TRADES: 'tradeCopilot_validatedTrades',
  SETTINGS: 'tradeCopilot_settings',
  PATTERN_ALERTS: 'tradeCopilot_patternAlerts'
};

// Simple in-memory cache to reduce localStorage reads (especially for mobile)
const memoryCache = {
  tradeJournal: null,
  tradeJournalTimestamp: 0,
  CACHE_TTL: 10000 // 10 seconds
};

const getCachedTradeJournal = () => {
  const now = Date.now();
  if (memoryCache.tradeJournal && (now - memoryCache.tradeJournalTimestamp) < memoryCache.CACHE_TTL) {
    return memoryCache.tradeJournal;
  }
  try {
    const data = localStorage.getItem(STORAGE_KEYS.TRADE_JOURNAL);
    memoryCache.tradeJournal = data ? JSON.parse(data) : [];
    memoryCache.tradeJournalTimestamp = now;
    return memoryCache.tradeJournal;
  } catch (error) {
    console.error('Error reading trade journal:', error);
    return [];
  }
};

// Invalidate cache when data changes
const invalidateTradeJournalCache = () => {
  memoryCache.tradeJournal = null;
  memoryCache.tradeJournalTimestamp = 0;
};

// Pattern Alert Tracking Functions
export const getPatternAlerts = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PATTERN_ALERTS);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading pattern alerts:', error);
    return [];
  }
};

export const savePatternAlert = (alertData) => {
  try {
    const alerts = getPatternAlerts();
    const newAlert = {
      id: `PA-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      signalId: alertData.signalId,
      asset: alertData.asset,
      signalType: alertData.signalType,
      timeframe: alertData.timeframe,
      historicalWinRate: alertData.historicalWinRate,
      patternLevel: alertData.patternLevel,
      timestamp: new Date().toISOString(),
      tradeLinked: false,
      linkedTradeId: null,
      tradeResult: null
    };
    alerts.unshift(newAlert);
    localStorage.setItem(STORAGE_KEYS.PATTERN_ALERTS, JSON.stringify(alerts));
    return newAlert;
  } catch (error) {
    console.error('Error saving pattern alert:', error);
    throw error;
  }
};

export const linkTradeToPatternAlert = (alertId, tradeId, tradeResult) => {
  try {
    const alerts = getPatternAlerts();
    const updated = alerts.map(alert => 
      alert.id === alertId 
        ? { ...alert, tradeLinked: true, linkedTradeId: tradeId, tradeResult, linkedAt: new Date().toISOString() }
        : alert
    );
    localStorage.setItem(STORAGE_KEYS.PATTERN_ALERTS, JSON.stringify(updated));
  } catch (error) {
    console.error('Error linking trade to pattern alert:', error);
  }
};

export const findPatternAlertBySignal = (signalId) => {
  try {
    const alerts = getPatternAlerts();
    return alerts.find(a => a.signalId === signalId && !a.tradeLinked);
  } catch (error) {
    console.error('Error finding pattern alert:', error);
    return null;
  }
};

export const getPatternPerformanceStats = () => {
  try {
    const alerts = getPatternAlerts();
    
    if (alerts.length === 0) {
      return {
        totalAlerts: 0,
        tradesTaken: 0,
        wins: 0,
        losses: 0,
        winRate: 0,
        accuracy: 0,
        byPattern: []
      };
    }
    
    const totalAlerts = alerts.length;
    const linkedAlerts = alerts.filter(a => a.tradeLinked);
    const tradesTaken = linkedAlerts.length;
    const wins = linkedAlerts.filter(a => a.tradeResult === 'win').length;
    const losses = linkedAlerts.filter(a => a.tradeResult === 'loss').length;
    const winRate = tradesTaken > 0 ? (wins / tradesTaken) * 100 : 0;
    
    // Calculate accuracy: alerts that predicted correctly
    // High probability alerts that resulted in wins = accurate
    const accurateAlerts = linkedAlerts.filter(a => {
      if (a.patternLevel === 'high' && a.tradeResult === 'win') return true;
      if (a.patternLevel === 'low' && a.tradeResult === 'loss') return true;
      return false;
    });
    const accuracy = tradesTaken > 0 ? (accurateAlerts.length / tradesTaken) * 100 : 0;
    
    // Group by pattern (signalType + timeframe)
    const patternGroups = {};
    alerts.forEach(alert => {
      const key = `${alert.signalType}-${alert.timeframe}`;
      if (!patternGroups[key]) {
        patternGroups[key] = {
          pattern: `${alert.signalType} (${alert.timeframe})`,
          signalType: alert.signalType,
          timeframe: alert.timeframe,
          alerts: 0,
          tradesTaken: 0,
          wins: 0,
          losses: 0
        };
      }
      patternGroups[key].alerts++;
      if (alert.tradeLinked) {
        patternGroups[key].tradesTaken++;
        if (alert.tradeResult === 'win') patternGroups[key].wins++;
        if (alert.tradeResult === 'loss') patternGroups[key].losses++;
      }
    });
    
    const byPattern = Object.values(patternGroups).map(p => ({
      ...p,
      winRate: p.tradesTaken > 0 ? ((p.wins / p.tradesTaken) * 100).toFixed(1) : '0.0',
      accuracy: p.tradesTaken > 0 
        ? ((p.wins / p.tradesTaken) * 100).toFixed(1) 
        : '0.0'
    })).sort((a, b) => b.alerts - a.alerts);
    
    // Get timeline data for charts
    const timelineData = getAlertTimelineData(alerts);
    
    return {
      totalAlerts,
      tradesTaken,
      wins,
      losses,
      winRate: winRate.toFixed(1),
      accuracy: accuracy.toFixed(1),
      byPattern,
      timelineData
    };
  } catch (error) {
    console.error('Error calculating pattern performance stats:', error);
    return {
      totalAlerts: 0,
      tradesTaken: 0,
      wins: 0,
      losses: 0,
      winRate: 0,
      accuracy: 0,
      byPattern: [],
      timelineData: []
    };
  }
};

const getAlertTimelineData = (alerts) => {
  // Group alerts by date
  const dateGroups = {};
  alerts.forEach(alert => {
    const date = new Date(alert.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!dateGroups[date]) {
      dateGroups[date] = {
        date,
        alerts: 0,
        trades: 0,
        wins: 0
      };
    }
    dateGroups[date].alerts++;
    if (alert.tradeLinked) {
      dateGroups[date].trades++;
      if (alert.tradeResult === 'win') dateGroups[date].wins++;
    }
  });
  
  return Object.values(dateGroups).reverse().slice(-14); // Last 14 days
};

// Automation Readiness Score Functions
export const calculateAutomationReadiness = () => {
  try {
    const trades = getTradeJournal();
    const closedTrades = trades.filter(t => t.result && (t.result === 'win' || t.result === 'loss'));
    
    if (closedTrades.length === 0) {
      return { patterns: [], recommendations: [] };
    }
    
    // Group trades by pattern (setupType + timeframe + asset)
    const patternGroups = {};
    closedTrades.forEach(trade => {
      const key = `${trade.setupType || 'unknown'}-${trade.timeframe || 'unknown'}-${trade.asset || 'unknown'}`;
      if (!patternGroups[key]) {
        patternGroups[key] = {
          pattern: key,
          setupType: trade.setupType || 'unknown',
          timeframe: trade.timeframe || 'unknown',
          asset: trade.asset || 'unknown',
          trades: [],
          displayName: `${trade.asset || 'Unknown'} ${trade.setupType || 'Unknown'} (${trade.timeframe || '?'})`
        };
      }
      patternGroups[key].trades.push(trade);
    });
    
    // Calculate metrics for each pattern
    const patterns = Object.values(patternGroups).map(group => {
      const totalTrades = group.trades.length;
      const wins = group.trades.filter(t => t.result === 'win').length;
      const losses = totalTrades - wins;
      const winRate = (wins / totalTrades) * 100;
      
      // Calculate average R:R
      const rrValues = group.trades
        .filter(t => t.riskRewardRatio)
        .map(t => parseFloat(t.riskRewardRatio));
      const avgRR = rrValues.length > 0 
        ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length 
        : 0;
      
      // Calculate expectancy (average R multiple)
      // Expectancy = (Win% × Avg Win) - (Loss% × Avg Loss)
      // Simplified: Expectancy = (WinRate × AvgRR) - (LossRate × 1)
      const expectancy = (winRate / 100 * avgRR) - ((100 - winRate) / 100 * 1);
      
      // Calculate drawdown consistency (standard deviation of results)
      const results = group.trades.map(t => {
        if (t.result === 'win') {
          return parseFloat(t.rewardPercent) || parseFloat(t.riskRewardRatio) || 1;
        } else {
          return -(parseFloat(t.riskPercent) || 1);
        }
      });
      
      const avgResult = results.reduce((a, b) => a + b, 0) / results.length;
      const variance = results.reduce((sum, r) => sum + Math.pow(r - avgResult, 2), 0) / results.length;
      const stdDev = Math.sqrt(variance);
      
      // Drawdown consistency score (lower std dev = more consistent)
      const consistencyScore = Math.max(0, 100 - (stdDev * 20));
      
      // Calculate performance trend (compare first half vs second half)
      const halfPoint = Math.floor(totalTrades / 2);
      const firstHalf = group.trades.slice(halfPoint);
      const secondHalf = group.trades.slice(0, halfPoint);
      const firstHalfWinRate = firstHalf.length > 0 
        ? (firstHalf.filter(t => t.result === 'win').length / firstHalf.length) * 100 
        : 0;
      const secondHalfWinRate = secondHalf.length > 0 
        ? (secondHalf.filter(t => t.result === 'win').length / secondHalf.length) * 100 
        : 0;
      const performanceTrend = secondHalfWinRate - firstHalfWinRate;
      
      // Calculate automation readiness score
      const readinessScore = calculateReadinessScore({
        totalTrades,
        winRate,
        expectancy,
        consistencyScore,
        performanceTrend,
        avgRR
      });
      
      return {
        ...group,
        totalTrades,
        wins,
        losses,
        winRate: winRate.toFixed(1),
        avgRR: avgRR.toFixed(2),
        expectancy: expectancy.toFixed(2),
        consistencyScore: consistencyScore.toFixed(0),
        performanceTrend: performanceTrend.toFixed(1),
        readinessScore: readinessScore.score,
        readinessLevel: readinessScore.level,
        readinessColor: readinessScore.color,
        meetsMinimum: readinessScore.meetsMinimum,
        failedCriteria: readinessScore.failedCriteria
      };
    });
    
    // Sort by readiness score descending
    patterns.sort((a, b) => b.readinessScore - a.readinessScore);
    
    // Generate recommendations
    const recommendations = generateAutomationRecommendations(patterns);
    
    return { patterns, recommendations };
  } catch (error) {
    console.error('Error calculating automation readiness:', error);
    return { patterns: [], recommendations: [] };
  }
};

const calculateReadinessScore = ({ totalTrades, winRate, expectancy, consistencyScore, performanceTrend, avgRR }) => {
  let score = 0;
  const failedCriteria = [];
  
  // Sample size score (0-25 points)
  // 50+ trades = full points, scales linearly
  const sampleScore = Math.min(25, (totalTrades / 50) * 25);
  score += sampleScore;
  if (totalTrades < 50) {
    failedCriteria.push(`Need ${50 - totalTrades} more trades (minimum 50)`);
  }
  
  // Win rate score (0-25 points)
  // 55%+ = full points, scales from 40%
  const winRateScore = Math.max(0, Math.min(25, ((winRate - 40) / 15) * 25));
  score += winRateScore;
  if (winRate < 55) {
    failedCriteria.push(`Win rate ${winRate.toFixed(1)}% below 55% threshold`);
  }
  
  // Expectancy score (0-25 points)
  // Positive expectancy required, scales up to 1.0R
  const expectancyScore = expectancy > 0 ? Math.min(25, (expectancy / 1) * 25) : 0;
  score += expectancyScore;
  if (expectancy <= 0) {
    failedCriteria.push(`Negative expectancy (${expectancy.toFixed(2)}R)`);
  }
  
  // Consistency/Stability score (0-25 points)
  // Based on consistency score and performance trend
  const stabilityScore = (consistencyScore * 0.15) + (performanceTrend > 0 ? 10 : (performanceTrend > -10 ? 5 : 0));
  score += Math.min(25, stabilityScore);
  if (performanceTrend < -10) {
    failedCriteria.push(`Declining performance trend (${performanceTrend.toFixed(1)}%)`);
  }
  
  // Determine level
  let level, color;
  if (score >= 90) {
    level = 'Automation Ready';
    color = '#10B981'; // success green
  } else if (score >= 70) {
    level = 'Monitor Closely';
    color = '#3B82F6'; // primary blue
  } else if (score >= 50) {
    level = 'Not Ready';
    color = '#F59E0B'; // warning yellow
  } else {
    level = 'Avoid';
    color = '#EF4444'; // destructive red
  }
  
  // Check if meets minimum requirements
  const meetsMinimum = totalTrades >= 50 && winRate >= 55 && expectancy > 0 && performanceTrend >= -10;
  
  return {
    score: Math.round(score),
    level,
    color,
    meetsMinimum,
    failedCriteria
  };
};

const generateAutomationRecommendations = (patterns) => {
  const recommendations = [];
  
  // Find patterns ready for automation
  const readyPatterns = patterns.filter(p => p.readinessScore >= 90 && p.meetsMinimum);
  if (readyPatterns.length > 0) {
    readyPatterns.forEach(p => {
      recommendations.push({
        type: 'success',
        message: `${p.displayName} shows strong consistency and meets automation criteria. Score: ${p.readinessScore}/100 with ${p.totalTrades} trades at ${p.winRate}% win rate.`
      });
    });
  }
  
  // Find patterns to monitor
  const monitorPatterns = patterns.filter(p => p.readinessScore >= 70 && p.readinessScore < 90);
  if (monitorPatterns.length > 0) {
    const best = monitorPatterns[0];
    recommendations.push({
      type: 'info',
      message: `${best.displayName} is close to automation ready (${best.readinessScore}/100). ${best.failedCriteria.length > 0 ? best.failedCriteria[0] : 'Continue monitoring for stability.'}`
    });
  }
  
  // Find patterns to avoid
  const avoidPatterns = patterns.filter(p => p.readinessScore < 50 && p.totalTrades >= 10);
  if (avoidPatterns.length > 0) {
    const worst = avoidPatterns[avoidPatterns.length - 1];
    recommendations.push({
      type: 'warning',
      message: `${worst.displayName} shows poor performance (${worst.readinessScore}/100). Consider reviewing your strategy for this pattern.`
    });
  }
  
  // General insight
  if (patterns.length > 0 && recommendations.length === 0) {
    const totalTrades = patterns.reduce((sum, p) => sum + p.totalTrades, 0);
    recommendations.push({
      type: 'info',
      message: `You have ${totalTrades} total trades across ${patterns.length} patterns. Most patterns need more data before automation decisions can be made.`
    });
  }
  
  return recommendations;
};

export const getMarketConditions = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.MARKET_CONDITIONS);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading market conditions:', error);
    return [];
  }
};

export const saveMarketCondition = (condition) => {
  try {
    const conditions = getMarketConditions();
    const newCondition = {
      ...condition,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    conditions.unshift(newCondition);
    localStorage.setItem(STORAGE_KEYS.MARKET_CONDITIONS, JSON.stringify(conditions));
    return newCondition;
  } catch (error) {
    console.error('Error saving market condition:', error);
    throw error;
  }
};

export const getTradeJournal = () => {
  return getCachedTradeJournal();
};

export const saveTradeEntry = (trade) => {
  try {
    const journal = getTradeJournal();
    const newTrade = {
      ...trade,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    journal.unshift(newTrade);
    localStorage.setItem(STORAGE_KEYS.TRADE_JOURNAL, JSON.stringify(journal));
    invalidateTradeJournalCache(); // Clear cache after save
    return newTrade;
  } catch (error) {
    console.error('Error saving trade entry:', error);
    throw error;
  }
};

export const deleteTradeEntry = (id) => {
  try {
    const journal = getTradeJournal();
    const filtered = journal.filter(trade => trade.id !== id);
    localStorage.setItem(STORAGE_KEYS.TRADE_JOURNAL, JSON.stringify(filtered));
    invalidateTradeJournalCache(); // Clear cache after delete
  } catch (error) {
    console.error('Error deleting trade entry:', error);
    throw error;
  }
};

export const updateTradeEntry = (id, patch) => {
  try {
    const journal = getTradeJournal();
    const idx = journal.findIndex((t) => t.id === id);
    if (idx === -1) return null;
    journal[idx] = { ...journal[idx], ...patch, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEYS.TRADE_JOURNAL, JSON.stringify(journal));
    invalidateTradeJournalCache();
    return journal[idx];
  } catch (error) {
    console.error('Error updating trade entry:', error);
    throw error;
  }
};

/**
 * Convert a closed paper-trade record into a journal entry shape.
 * Status mapping:
 *   closed_tp → win
 *   closed_sl → loss
 *   closed_manual → win/loss based on pnl (or breakeven)
 */
const _paperTradeToJournalEntry = (pt) => {
  let result = 'breakeven';
  if (pt.status === 'closed_tp') result = 'win';
  else if (pt.status === 'closed_sl') result = 'loss';
  else if (pt.status === 'closed_manual') {
    if (pt.pnl > 0) result = 'win';
    else if (pt.pnl < 0) result = 'loss';
  }

  const direction = pt.direction || (pt.signal_type ? pt.signal_type.toLowerCase() : '');
  const setupType = direction === 'short' || direction === 'sell' ? 'short' : 'long';

  return {
    id: `pt-${pt.id}`,
    paperTradeId: pt.id,
    source: 'paper_trading',
    timestamp: pt.closed_at || pt.created_at || new Date().toISOString(),
    openedAt: pt.opened_at || null,
    asset: pt.symbol || '',
    setupType,
    timeframe: pt.timeframe || '',
    entryPrice: pt.entry_price ?? '',
    exitPrice: pt.close_price ?? '',
    stopLoss: pt.stop_loss ?? '',
    takeProfit: pt.take_profit ?? '',
    result,
    pnl: pt.pnl ?? null,
    pnlPercent: pt.pnl_percent ?? null,
    closeReason: pt.close_reason || '',
    automationScore: pt.automation_score ?? null,
    riskRewardRatio: (() => {
      if (!pt.entry_price || !pt.stop_loss || !pt.take_profit) return null;
      const risk = Math.abs(pt.entry_price - pt.stop_loss);
      const reward = Math.abs(pt.take_profit - pt.entry_price);
      return risk > 0 ? (reward / risk).toFixed(2) : null;
    })(),
    emotion: '',
    mistake: '',
    lesson: '',
    notes: '',
    chartScreenshot: null,
    ruleViolation: false,
    isPlanned: true, // auto-executed signals are by definition rule-following
    isUnplanned: false,
  };
};

/**
 * Auto-import closed paper trades into the journal, deduped by paperTradeId.
 * Also backfills missing `openedAt` on already-imported entries so older
 * journal rows get the open timestamp once available.
 * Returns the count of newly imported entries.
 */
export const importPaperTradesIntoJournal = (paperTrades) => {
  try {
    const journal = getTradeJournal();
    const ptById = new Map(
      (paperTrades || []).filter((pt) => pt && pt.id).map((pt) => [pt.id, pt])
    );
    const existing = new Set(
      journal
        .filter((t) => t.source === 'paper_trading' && t.paperTradeId)
        .map((t) => t.paperTradeId)
    );

    // Backfill openedAt on existing entries that are missing it
    let backfilled = 0;
    const patchedJournal = journal.map((t) => {
      if (
        t.source === 'paper_trading' &&
        t.paperTradeId &&
        !t.openedAt &&
        ptById.get(t.paperTradeId)?.opened_at
      ) {
        backfilled += 1;
        return { ...t, openedAt: ptById.get(t.paperTradeId).opened_at };
      }
      return t;
    });

    const toImport = (paperTrades || [])
      .filter(
        (pt) =>
          pt &&
          pt.id &&
          ['closed_tp', 'closed_sl', 'closed_manual'].includes(pt.status) &&
          !existing.has(pt.id)
      )
      .map(_paperTradeToJournalEntry);

    if (toImport.length === 0 && backfilled === 0) return 0;

    const merged = [...toImport, ...patchedJournal].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    localStorage.setItem(STORAGE_KEYS.TRADE_JOURNAL, JSON.stringify(merged));
    invalidateTradeJournalCache();
    return toImport.length;
  } catch (error) {
    console.error('Error importing paper trades into journal:', error);
    return 0;
  }
};

export const getTradingPlan = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.TRADING_PLAN);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading trading plan:', error);
    return null;
  }
};

export const saveTradingPlan = (plan) => {
  try {
    localStorage.setItem(STORAGE_KEYS.TRADING_PLAN, JSON.stringify(plan));
    return plan;
  } catch (error) {
    console.error('Error saving trading plan:', error);
    throw error;
  }
};

export const getValidatedTrades = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.VALIDATED_TRADES);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading validated trades:', error);
    return [];
  }
};

export const saveValidatedTrade = (tradeData) => {
  try {
    const validatedTrades = getValidatedTrades();
    const preTradeId = `PT-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    const validatedTrade = {
      preTradeId,
      ...tradeData,
      validatedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours validity
    };
    
    validatedTrades.push(validatedTrade);
    localStorage.setItem(STORAGE_KEYS.VALIDATED_TRADES, JSON.stringify(validatedTrades));
    
    return preTradeId;
  } catch (error) {
    console.error('Error saving validated trade:', error);
    throw error;
  }
};

export const getValidatedTradeById = (preTradeId) => {
  try {
    const validatedTrades = getValidatedTrades();
    const trade = validatedTrades.find(t => t.preTradeId === preTradeId);
    
    if (!trade) return null;
    
    // Check if expired
    const expiresAt = new Date(trade.expiresAt);
    if (new Date() > expiresAt) {
      return { ...trade, expired: true };
    }
    
    return trade;
  } catch (error) {
    console.error('Error getting validated trade:', error);
    return null;
  }
};

export const markValidatedTradeAsUsed = (preTradeId) => {
  try {
    const validatedTrades = getValidatedTrades();
    const updated = validatedTrades.map(t => 
      t.preTradeId === preTradeId ? { ...t, used: true, usedAt: new Date().toISOString() } : t
    );
    localStorage.setItem(STORAGE_KEYS.VALIDATED_TRADES, JSON.stringify(updated));
  } catch (error) {
    console.error('Error marking validated trade as used:', error);
  }
};

export const cleanupExpiredValidations = () => {
  try {
    const validatedTrades = getValidatedTrades();
    const now = new Date();
    const active = validatedTrades.filter(t => {
      const expiresAt = new Date(t.expiresAt);
      return now <= expiresAt || t.used; // Keep used ones for reference
    });
    localStorage.setItem(STORAGE_KEYS.VALIDATED_TRADES, JSON.stringify(active));
  } catch (error) {
    console.error('Error cleaning up expired validations:', error);
  }
};

// Alert Settings functions
export const getAlertSettings = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    const settings = data ? JSON.parse(data) : {};
    return {
      patternAlertsEnabled: settings.patternAlertsEnabled ?? true,
      minWinRateThreshold: settings.minWinRateThreshold ?? 60,
      minTradesRequired: settings.minTradesRequired ?? 10,
      ...settings
    };
  } catch (error) {
    console.error('Error reading alert settings:', error);
    return {
      patternAlertsEnabled: true,
      minWinRateThreshold: 60,
      minTradesRequired: 10
    };
  }
};

export const saveAlertSettings = (settings) => {
  try {
    const currentSettings = getAlertSettings();
    const updatedSettings = { ...currentSettings, ...settings };
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(updatedSettings));
    return updatedSettings;
  } catch (error) {
    console.error('Error saving alert settings:', error);
    throw error;
  }
};

// Pattern analysis for Signal Pattern Alerts
export const analyzePatternPerformance = (signalType, timeframe, asset) => {
  try {
    const trades = getTradeJournal();
    const closedTrades = trades.filter(t => t.result && (t.result === 'win' || t.result === 'loss'));
    
    if (closedTrades.length === 0) {
      return { hasData: false, insufficientData: true };
    }
    
    // Find matching trades based on available criteria
    let matchingTrades = closedTrades;
    
    // Filter by signal type if provided
    if (signalType) {
      matchingTrades = matchingTrades.filter(t => 
        t.setupType && t.setupType.toLowerCase() === signalType.toLowerCase()
      );
    }
    
    // Filter by timeframe if provided
    if (timeframe) {
      matchingTrades = matchingTrades.filter(t => 
        t.timeframe && t.timeframe.toLowerCase() === timeframe.toLowerCase()
      );
    }
    
    // Filter by asset if provided
    if (asset) {
      matchingTrades = matchingTrades.filter(t => 
        t.asset && t.asset.toLowerCase() === asset.toLowerCase()
      );
    }
    
    const totalTrades = matchingTrades.length;
    
    if (totalTrades === 0) {
      return { hasData: false, insufficientData: true, totalTrades: 0 };
    }
    
    const wins = matchingTrades.filter(t => t.result === 'win').length;
    const winRate = (wins / totalTrades) * 100;
    
    // Calculate average R:R
    const rrValues = matchingTrades
      .filter(t => t.riskRewardRatio)
      .map(t => parseFloat(t.riskRewardRatio));
    const avgRR = rrValues.length > 0 
      ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length 
      : 0;
    
    // Calculate total P&L
    let totalPL = 0;
    matchingTrades.forEach(trade => {
      if (trade.result === 'win') {
        totalPL += parseFloat(trade.rewardPercent) || 0;
      } else {
        totalPL -= parseFloat(trade.riskPercent) || 0;
      }
    });
    
    return {
      hasData: true,
      insufficientData: totalTrades < 10,
      totalTrades,
      wins,
      losses: totalTrades - wins,
      winRate: winRate.toFixed(1),
      avgRR: avgRR.toFixed(2),
      totalPL: totalPL.toFixed(2),
      signalType,
      timeframe,
      asset
    };
  } catch (error) {
    console.error('Error analyzing pattern performance:', error);
    return { hasData: false, insufficientData: true, error: true };
  }
};

// Get pattern probability level
export const getPatternProbability = (signalType, timeframe, asset, settings = null) => {
  const alertSettings = settings || getAlertSettings();
  const analysis = analyzePatternPerformance(signalType, timeframe, asset);
  
  if (!analysis.hasData || analysis.insufficientData) {
    return {
      level: 'insufficient',
      label: 'Insufficient Data',
      color: 'gray',
      analysis
    };
  }
  
  const winRate = parseFloat(analysis.winRate);
  const minThreshold = alertSettings.minWinRateThreshold;
  
  if (winRate >= minThreshold) {
    return {
      level: 'high',
      label: 'High Probability',
      color: 'green',
      analysis
    };
  } else if (winRate >= minThreshold - 15) {
    return {
      level: 'moderate',
      label: 'Moderate',
      color: 'yellow',
      analysis
    };
  } else {
    return {
      level: 'low',
      label: 'Low Probability',
      color: 'red',
      analysis
    };
  }
};

export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]).filter(key => key !== 'chartScreenshot');
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};